import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Custom hook for managing drag and drop functionality
 */
export const useDragAndDrop = ({
  selectedItem,
  detailsRows,
  setDetailsRow,
  queryKey,
  detailSheetType,
  refetchSwimlaneCosts,
  selectedBudget,
  useReorderDetailSheet,
  useReorderDetailLineItemSheet,
}) => {
  const queryClient = useQueryClient();

  // Drag start handler
  const onDragStart = useCallback(() => {
    // Drag start handler
  }, []);

  // Drag end handler
  const onDragEnd = useCallback(
    result => {
      if (!result.destination) return;

      const reorderedItems = Array.from(detailsRows[selectedItem?.id] || []);
      const [movedItem] = reorderedItems.splice(result.source.index, 1);
      reorderedItems.splice(result.destination.index, 0, movedItem);
      const adjustedItems = reorderedItems.map((item, i) => ({
        ...item,
        position: i,
        row_order: i + 1,
      }));
      setDetailsRow(prev => ({ ...prev, [selectedItem?.id]: adjustedItems }));

      const payload = [];
      adjustedItems.forEach(item => {
        if (item?.id) {
          payload.push({ id: item.id, position: item.position });
        }
      });

      if (payload.length === 0) return;

      // Use view type to choose reorder endpoint (row ids are numeric, not string keywords)
      const isSwimlaneDetailSheet = detailSheetType === 'swimlane';
      const isLineItemDetailSheet =
        detailSheetType === 'line-item' ||
        detailSheetType === 'line-item-children';

      if (isSwimlaneDetailSheet) {
        useReorderDetailSheet.mutate(
          { detail_sheet_items: payload },
          {
            onSuccess: () => {
              refetchSwimlaneCosts?.({ budgetId: selectedBudget?.budget });
            },
            onError: () => {
              // Handle error silently
            },
            onSettled: () => {
              queryClient.invalidateQueries(queryKey);
            },
          }
        );
      } else if (isLineItemDetailSheet) {
        useReorderDetailLineItemSheet.mutate(
          { detail_lineitem_sheet_items: payload },
          {
            onSuccess: () => {
              refetchSwimlaneCosts?.({ budgetId: selectedBudget?.budget });
            },
            onError: () => {
              // Handle error silently
            },
            onSettled: () => {
              queryClient.invalidateQueries(queryKey);
            },
          }
        );
      }
    },
    [
      detailsRows,
      selectedItem,
      setDetailsRow,
      queryClient,
      queryKey,
      detailSheetType,
      refetchSwimlaneCosts,
      selectedBudget,
      useReorderDetailSheet,
      useReorderDetailLineItemSheet,
    ]
  );

  // Reorder rows function (used e.g. by keyboard nav)
  const reorderRows = useCallback(
    rows => {
      const payload = [];
      rows.forEach((item, index) => {
        if (item?.id) {
          payload.push({ id: item.id, position: index });
        }
      });

      if (payload.length === 0) return;

      const isSwimlaneDetailSheet = detailSheetType === 'swimlane';
      const isLineItemDetailSheet =
        detailSheetType === 'line-item' ||
        detailSheetType === 'line-item-children';

      if (isSwimlaneDetailSheet) {
        useReorderDetailSheet.mutate(
          { detail_sheet_items: payload },
          {
            onSuccess: () => {
              refetchSwimlaneCosts?.({ budgetId: selectedBudget?.budget });
            },
            onError: () => {
              // Handle error silently
            },
            onSettled: () => {
              queryClient.invalidateQueries(queryKey);
            },
          }
        );
      } else if (isLineItemDetailSheet) {
        useReorderDetailLineItemSheet.mutate(
          { detail_lineitem_sheet_items: payload },
          {
            onSuccess: () => {
              refetchSwimlaneCosts?.({ budgetId: selectedBudget?.budget });
            },
            onError: () => {
              // Handle error silently
            },
            onSettled: () => {
              queryClient.invalidateQueries(queryKey);
            },
          }
        );
      }
    },
    [
      detailSheetType,
      useReorderDetailSheet,
      useReorderDetailLineItemSheet,
      queryClient,
      queryKey,
      refetchSwimlaneCosts,
      selectedBudget,
    ]
  );

  return {
    onDragStart,
    onDragEnd,
    reorderRows,
  };
};
